import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { faArrowUpFromBracket, faCode } from '@fortawesome/pro-solid-svg-icons'
import { FunDropdownMenu, type FunMenuItem } from '@unwrittenmedia/simple-components'
import { useGQL_team, type TAPITeam, type TAPIPlace } from '@/contexts/graphql-and-swr-apis'
import { MainPageFooter } from '@/components/MainPageFooter'
import { CopyableTextArea } from '@/components/CopyableTextArea'
import SimpleModalDialog from '@/components/SimpleModalDialog'

export const Route = createFileRoute('/t/$teamId')({
  component: PublicTeamPage,
})

function PublicTeamPage() {
  const { teamId } = Route.useParams()
  const { data, isLoading, error } = useGQL_team(teamId, { publicOnly: true })
  const team = data?.team

  if (isLoading) {
    return <PageMessage>Loading team…</PageMessage>
  }

  if (error) {
    return <PageMessage tone="error">Unable to load this team. Please try again later.</PageMessage>
  }

  if (!team) {
    return <PageMessage>This team could not be found.</PageMessage>
  }

  const teamUrl = `${window.location.origin}/t/${team.handle || team.id}`

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900 dark:bg-mist-950 dark:text-white">
      <div aria-hidden="true" className="h-44 bg-gradient-to-r from-indigo-900 via-violet-800 to-sky-700 sm:h-56" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pb-12 sm:px-6 lg:grid-cols-[17rem_minmax(0,1fr)] lg:gap-10 lg:px-8 mb-12">
        <TeamProfile team={team} teamUrl={teamUrl} />

        <div className="min-w-0 pt-1 lg:pt-8">
          <PlacesSection places={team.places ?? []} />
        </div>
      </div>
      <MainPageFooter />
    </main>
  )
}

/** Left (top on mobile) section displaying the team's profile */
function TeamProfile({ team, teamUrl }: { team: TAPITeam; teamUrl: string }) {
  const initial = team.name.trim().charAt(0).toUpperCase() || '?'
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: team.name, text: team.description || undefined, url: teamUrl })
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') console.error(error)
      }
    } else if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(teamUrl)
        window.alert('Team link copied to clipboard')
      } catch (error) {
        console.error(error)
      }
    }
  }

  return (
    <aside className="-mt-16 self-start rounded-2xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-900/5 dark:border-mist-800 dark:bg-mist-900 lg:-mt-16">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-indigo-600 text-3xl font-semibold text-white ring-4 ring-white dark:ring-mist-900">
        {initial}
      </div>
      <h1 className="mt-5 text-2xl font-semibold tracking-tight">{team.name}</h1>

      <div className="mt-5 border-t border-slate-100 pt-5 dark:border-mist-800">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-mist-400">About</h2>
        <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600 dark:text-mist-300">
          {team.description || 'More information about this team will be added soon.'}
        </p>
        <a
          href={teamUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex max-w-full items-center gap-2 break-all text-sm font-medium text-indigo-700 hover:text-indigo-900 dark:text-indigo-300 dark:hover:text-indigo-200"
        >
          <svg aria-hidden="true" className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path d="M11.5 2a.75.75 0 000 1.5h3.94L9.22 9.72a.75.75 0 101.06 1.06L16.5 4.56v3.94a.75.75 0 001.5 0V2.75a.75.75 0 00-.75-.75H11.5z" />
            <path d="M4.75 4A2.75 2.75 0 002 6.75v8.5A2.75 2.75 0 004.75 18h8.5A2.75 2.75 0 0016 15.25V11.5a.75.75 0 00-1.5 0v3.75c0 .69-.56 1.25-1.25 1.25h-8.5c-.69 0-1.25-.56-1.25-1.25v-8.5c0-.69.56-1.25 1.25-1.25H8.5a.75.75 0 000-1.5H4.75z" />
          </svg>
          {team.handle ? `${window.location.host}/t/${team.handle}` : 'View team page'}
        </a>
      </div>
      <button
        type="button"
        onClick={handleShare}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 px-4 py-2.5 text-sm font-medium text-indigo-700 transition hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-mist-700 dark:text-indigo-300 dark:hover:bg-mist-800"
      >
        <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path d="M15.5 13a2.5 2.5 0 00-2.24 1.39l-5.1-2.92a2.5 2.5 0 000-2.94l5.1-2.92A2.5 2.5 0 1012.5 4c0 .14.01.28.04.41l-5.1 2.92a2.5 2.5 0 100 5.34l5.1 2.92A2.5 2.5 0 1015.5 13z" />
        </svg>
        Share team
      </button>
    </aside>
  )
}

function PlacesSection({ places }: { places: TAPIPlace[] }) {
  return (
    <section aria-labelledby="places-heading">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h2 id="places-heading" className="text-2xl font-semibold tracking-tight">Places</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-mist-400">
            {places.length} {places.length === 1 ? 'place' : 'places'}
          </p>
        </div>
      </div>

      {places.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {places.map((place) => <PlaceCard key={place.id} place={place} />)}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center dark:border-mist-700 dark:bg-mist-900">
          <p className="text-sm text-slate-500 dark:text-mist-400">There are no public places to show yet.</p>
        </div>
      )}
    </section>
  )
}

function PlaceCard({ place }: { place: TAPIPlace }) {
  const [showEmbedDialog, setShowEmbedDialog] = useState(false)
  const placeIdentifier = place.place_code || place.id
  const watchUrl = `${window.location.origin}/watch/${placeIdentifier}`
  const embedUrl = `${window.location.origin}/embed/${placeIdentifier}/latest`
  const menuItems: FunMenuItem[] = [
    { id: 'share', label: 'Share', icon: faArrowUpFromBracket },
    { id: 'embed', label: 'Embed', icon: faCode },
  ]

  const handleMenuAction = async (action: string) => {
    if (action === 'embed') {
      setShowEmbedDialog(true)
    } else if (action === 'share') {
      try {
        if (navigator.share) {
          await navigator.share({ title: place.name, url: watchUrl })
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(watchUrl)
          window.alert('Place link copied to clipboard')
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') console.error(error)
      }
    }
  }

  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition  hover:shadow-md dark:border-mist-800 dark:bg-mist-900">
      <a href={watchUrl} aria-label={`Watch ${place.name}`} className="block">
        <div
          aria-hidden="true"
          className="aspect-[16/10] bg-cover bg-center bg-slate-200 dark:bg-mist-800"
          style={{ backgroundImage: `url("/api/place-thumbnail-redir/${encodeURIComponent(place.id)}")` }}
        />
      </a>
      <div className="flex items-center gap-2 px-4 py-3">
        <a href={watchUrl} className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-slate-900 dark:text-white">{place.name}</h3>
        </a>
        <FunDropdownMenu items={menuItems} onAction={handleMenuAction} user={place.id} />
      </div>
      <SimpleModalDialog
        open={showEmbedDialog}
        onClose={() => setShowEmbedDialog(false)}
        dialogTitle={`Embed ${place.name}`}
        icon={faCode}
        iconColor="indigo"
        BodyComponent={EmbedDialogBody}
        data={{ iframeCode: `<iframe src="${embedUrl}" title="Live sermon slides" style="width: 100%; aspect-ratio: 16 / 9; border: 0;" allowfullscreen></iframe>` }}
        buttons={[{ id: 'close', label: 'Close', color: 'secondary', size: 'md' }]}
      />
    </article>
  )
}

function EmbedDialogBody({ data }: { data: { iframeCode: string } }) {
  return (
    <div className="text-left">
      <p className="mb-2 text-sm text-slate-600 dark:text-mist-300">
        Copy this iframe code to embed this place’s live slides on your website.
      </p>
      <CopyableTextArea text={data.iframeCode} />
    </div>
  )
}

function PageMessage({ children, tone = 'normal' }: { children: React.ReactNode; tone?: 'normal' | 'error' }) {
  return (
    <main className="flex min-h-[60vh] items-center justify-center bg-slate-50 px-4 dark:bg-mist-950">
      <p className={`text-sm ${tone === 'error' ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-mist-400'}`}>
        {children}
      </p>
    </main>
  )
}
